import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { DataCard, DataCardActions, DataCardEmpty, DataCardRow } from '../../components/ui/DataCard';
import { Switch } from '../../components/ui/Switch';
import { TextField } from '../../components/forms/TextField';
import { cn } from '../../utils/cn';
import { projectApi, type ProjectEntity } from '../../api/ProjectApi';
import { roleAccessApi, type RoleAccessEntity } from '../../api/RoleAccessApi';
import { useAuth } from '../../auth/AuthContext';
import { useI18n } from '../../i18n/I18nContext';

function Badge({ children, tone }: { children: React.ReactNode; tone: 'green' | 'amber' | 'slate' | 'red' | 'rose' }) {
  const tones: Record<typeof tone, string> = {
    green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
  };
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', tones[tone])}>
      {children}
    </span>
  );
}

/* ───────── Permission module flags on RoleAccess entity ───────── */
const MODULE_FLAGS = [
  { key: 'dashboard', labelKey: 'dashboard' },
  { key: 'booking', labelKey: 'booking' },
  { key: 'ecommerce', labelKey: 'ecommerce' },
  { key: 'user', labelKey: 'user' },
  { key: 'customer', labelKey: 'customer' },
  { key: 'notification', labelKey: 'notification' },
  { key: 'custom', labelKey: 'custom' },
  { key: 'reward', labelKey: 'reward' },
  { key: 'rental', labelKey: 'rental' },
  { key: 'project', labelKey: 'project' },
] as const;

type ModuleKey = typeof MODULE_FLAGS[number]['key'];

type RoleDraft = {
  roleAccessId?: string;
  role: string;
} & Record<ModuleKey, boolean>;

function emptyRoleDraft(): RoleDraft {
  const flags = {} as Record<ModuleKey, boolean>;
  for (const f of MODULE_FLAGS) flags[f.key] = false;
  return { role: '', ...flags };
}

function roleEntityToDraft(entity: RoleAccessEntity): RoleDraft {
  const flags = {} as Record<ModuleKey, boolean>;
  for (const f of MODULE_FLAGS) flags[f.key] = !!(entity as any)[f.key];
  return { roleAccessId: entity.roleAccessId, role: entity.role, ...flags };
}

/* ───────── Roles every new project starts with ───────── */
const DEFAULT_ROLE_SEEDS: Array<{ role: string; modules: ModuleKey[] }> = [
  { role: 'owner', modules: ['dashboard', 'user', 'customer', 'booking'] },
  { role: 'admin', modules: ['user', 'customer', 'booking'] },
  { role: 'staff', modules: [] },
];

function defaultRoleSeeds(): RoleDraft[] {
  return DEFAULT_ROLE_SEEDS.map(({ role, modules }) => {
    const draft = { ...emptyRoleDraft(), role };
    for (const module of modules) draft[module] = true;
    return draft;
  });
}

/** Login the backend mints for each seeded role. */
function seedUserEmail(projectCode: string, role: string) {
  return `${(projectCode || 'code').trim().toLowerCase()}.${role}@batamx.co`;
}

/* ───────── Project draft ───────── */
type ProjectDraft = {
  projectId?: string;
  name: string;
  code: string;
  description: string;
  sector: string;
  address: string;
  email: string;
  contactPerson: string;
  contactNo: string;
  country: string;
  currency: string;
  isTest: boolean;
  isActive: boolean;
};

const EMPTY_DRAFT: ProjectDraft = {
  name: '',
  code: '',
  description: '',
  sector: '',
  address: '',
  email: '',
  contactPerson: '',
  contactNo: '',
  country: 'ID',
  currency: 'IDR',
  isTest: true,
  isActive: true,
};

const DEBOUNCE_MS = 300;

export function ProjectsPage() {
  const { t } = useI18n();
  const { switchProject } = useAuth();
  const navigate = useNavigate();

  /* ── Projects state ── */
  const [allProjects, setAllProjects] = useState<ProjectEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [limit, setLimit] = useState(10);
  const [skip, setSkip] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [draft, setDraft] = useState<ProjectDraft>({ ...EMPTY_DRAFT });
  const [roleSeeds, setRoleSeeds] = useState<RoleDraft[]>(defaultRoleSeeds);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Roles state ── */
  const [rolesModalOpen, setRolesModalOpen] = useState(false);
  const [rolesProject, setRolesProject] = useState<ProjectEntity | null>(null);
  const [roles, setRoles] = useState<RoleAccessEntity[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleEditing, setRoleEditing] = useState<RoleDraft | null>(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  /* ── Projects loading ── */
  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await projectApi.list();
      setAllProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('projects.loadFailed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const filtered = useMemo(() => {
    if (!debouncedQuery) return allProjects;
    const q = debouncedQuery.toLowerCase();
    return allProjects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.code.toLowerCase().includes(q) ||
        (p.sector ?? '').toLowerCase().includes(q) ||
        (p.contactPerson ?? '').toLowerCase().includes(q),
    );
  }, [allProjects, debouncedQuery]);

  const total = filtered.length;
  const page = useMemo(() => filtered.slice(skip, skip + limit), [filtered, skip, limit]);

  function handleSearchChange(value: string) {
    setQuery(value);
    setSkip(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, DEBOUNCE_MS);
  }

  function handlePageChange(next: { skip: number; limit: number }) {
    setSkip(next.skip);
    setLimit(next.limit);
  }

  function refresh() {
    loadProjects();
  }

  function openCreate() {
    setError(null);
    setDraft({ ...EMPTY_DRAFT });
    setRoleSeeds(defaultRoleSeeds());
    setModalOpen(true);
  }

  /** Enable/disable straight from the row, so taking a booking site down is one click. */
  async function toggleProjectActive(proj: ProjectEntity, isActive: boolean) {
    setError(null);
    setTogglingId(proj.projectId);
    setAllProjects((projects) =>
      projects.map((p) => (p.projectId === proj.projectId ? { ...p, isActive } : p)),
    );
    try {
      await projectApi.update(proj.projectId, { isActive });
      showToast(t(isActive ? 'projects.toast.enabled' : 'projects.toast.disabled', { name: proj.name }));
    } catch (err) {
      setAllProjects((projects) =>
        projects.map((p) => (p.projectId === proj.projectId ? { ...p, isActive: proj.isActive } : p)),
      );
      setError(err instanceof Error ? err.message : t('common.saveFailed'));
    } finally {
      setTogglingId(null);
    }
  }

  function toggleSeedModule(role: string, module: ModuleKey, enabled: boolean) {
    setRoleSeeds((seeds) =>
      seeds.map((seed) => (seed.role === role ? { ...seed, [module]: enabled } : seed)),
    );
  }

  const seedsBooking = roleSeeds.some((seed) => seed.booking);

  function openEdit(proj: ProjectEntity) {
    setError(null);
    setDraft({
      projectId: proj.projectId,
      name: proj.name,
      code: proj.code,
      description: proj.description ?? '',
      sector: proj.sector ?? '',
      address: proj.address ?? '',
      email: proj.email ?? '',
      contactPerson: proj.contactPerson ?? '',
      contactNo: proj.contactNo ?? '',
      country: proj.country ?? 'ID',
      currency: proj.currency ?? 'IDR',
      isTest: proj.isTest ?? false,
      isActive: proj.isActive ?? true,
    });
    setModalOpen(true);
  }

  async function handleAccess(proj: ProjectEntity) {
    const ok = window.confirm(t('projects.confirmSwitch', { name: proj.name }));
    if (!ok) return;
    try {
      await switchProject(proj.projectId);
      navigate('/app/dashboard', { replace: true });
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('projects.switchFailed'));
    }
  }

  async function saveDraft() {
    setError(null);
    const name = draft.name.trim();
    const code = draft.code.trim();
    if (!name) return setError(t('projects.validation.nameRequired'));
    if (!code) return setError(t('projects.validation.codeRequired'));
    if (!draft.sector.trim()) return setError(t('projects.validation.sectorRequired'));
    if (!draft.contactPerson.trim()) return setError(t('projects.validation.contactPersonRequired'));
    if (!draft.contactNo.trim()) return setError(t('projects.validation.contactNoRequired'));

    const payload: Record<string, unknown> = {
      name,
      code,
      description: draft.description.trim() || undefined,
      sector: draft.sector.trim(),
      address: draft.address.trim() || undefined,
      email: draft.email.trim() || undefined,
      contactPerson: draft.contactPerson.trim(),
      contactNo: draft.contactNo.trim(),
      country: draft.country,
      currency: draft.currency,
      isTest: draft.isTest,
      isActive: draft.isActive,
    };

    setSaving(true);
    try {
      if (draft.projectId) {
        await projectApi.update(draft.projectId, payload);
      } else {
        // The backend seeds a role access, a user, and — when booking is granted —
        // a placeholder catalogue with everyday booking hours, for each role here.
        payload.roles = roleSeeds.map(({ roleAccessId, ...seed }) => seed);
        await projectApi.create(payload);
      }
      setModalOpen(false);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  /* ── Role Access CRUD ── */
  const loadRoles = useCallback(async (projectId: string) => {
    setRolesLoading(true);
    try {
      const { data: res } = await roleAccessApi.list(projectId);
      setRoles(res.data);
    } catch (err) {
      setRoleError(err instanceof Error ? err.message : t('projects.roles.loadFailed'));
    } finally {
      setRolesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openRolesModal(proj: ProjectEntity) {
    setRoleError(null);
    setRolesProject(proj);
    setRolesModalOpen(true);
    loadRoles(proj.projectId);
  }

  function openCreateRole() {
    setRoleError(null);
    setRoleEditing(emptyRoleDraft());
    setRoleModalOpen(true);
  }

  function openEditRole(entity: RoleAccessEntity) {
    setRoleError(null);
    setRoleEditing(roleEntityToDraft(entity));
    setRoleModalOpen(true);
  }

  async function saveRole() {
    if (!roleEditing || !rolesProject) return;
    setRoleError(null);
    const roleName = roleEditing.role.trim().toLowerCase();
    if (!roleName) return setRoleError(t('projects.roles.roleNameRequired'));

    const flags: Record<string, boolean> = {};
    for (const f of MODULE_FLAGS) flags[f.key] = roleEditing[f.key];

    setRoleSaving(true);
    try {
      if (roleEditing.roleAccessId) {
        await roleAccessApi.update(roleEditing.roleAccessId, { role: roleName, ...flags });
      } else {
        await roleAccessApi.create({
          role: roleName,
          ...flags,
          projectId: rolesProject.projectId,
          projectCode: rolesProject.code,
        });
      }
      setRoleModalOpen(false);
      showToast(roleEditing.roleAccessId ? t('projects.roles.roleUpdated') : t('projects.roles.roleCreated'));
      await loadRoles(rolesProject.projectId);
    } catch (err) {
      setRoleError(err instanceof Error ? err.message : t('common.saveFailed'));
    } finally {
      setRoleSaving(false);
    }
  }

  async function deleteRole(entity: RoleAccessEntity) {
    if (!rolesProject) return;
    const ok = window.confirm(t('projects.roles.confirmDelete', { role: entity.role }));
    if (!ok) return;
    try {
      await roleAccessApi.delete(entity.roleAccessId);
      showToast(t('projects.roles.roleDeleted'));
      await loadRoles(rolesProject.projectId);
    } catch (err) {
      setRoleError(err instanceof Error ? err.message : t('common.deleteFailed'));
    }
  }

  const enabledCount = (entity: RoleAccessEntity) =>
    MODULE_FLAGS.filter((f) => (entity as any)[f.key]).length;

  const moduleLabel = (key: ModuleKey) => t(`projects.roles.modules.${key}`);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{t('projects.title')}</div>
          <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {t('projects.subtitle')}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 [&>button]:flex-1 sm:[&>button]:flex-none">
          <Button variant="secondary" onClick={refresh} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </Button>
          <Button onClick={openCreate}>{t('projects.addProject')}</Button>
        </div>
      </div>

      {toast && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
          {toast}
        </div>
      )}

      {error && !modalOpen ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:max-w-sm">
            <TextField
              label={t('common.search')}
              placeholder={t('projects.searchPlaceholder')}
              value={query}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {t('common.total')} <span className="font-semibold text-slate-700 dark:text-slate-300">{total}</span>
          </div>
        </div>

        {/* Mobile card list */}
        <div className="mt-4 space-y-3 md:hidden">
          {loading && page.length === 0 ? (
            <DataCardEmpty>{t('projects.loadingProjects')}</DataCardEmpty>
          ) : page.length === 0 ? (
            <DataCardEmpty>{t('common.noResults')}</DataCardEmpty>
          ) : (
            page.map((p) => (
              <DataCard key={p.projectId}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-900 dark:text-slate-100">{p.name}</div>
                    <div className="truncate font-mono text-xs text-slate-500 dark:text-slate-400">{p.code}</div>
                  </div>
                  <Badge tone={p.isTest ? 'amber' : 'green'}>
                    {p.isTest ? t('projects.badges.test') : t('projects.badges.live')}
                  </Badge>
                </div>
                <div className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                  <DataCardRow label={t('projects.table.sector')}>
                    {p.sector || t('common.notAvailable')}
                  </DataCardRow>
                  <DataCardRow label={t('projects.table.contact')}>
                    <div>{p.contactPerson || t('common.notAvailable')}</div>
                    {p.contactNo && <div className="text-xs text-slate-500 dark:text-slate-400">{p.contactNo}</div>}
                    {p.email && <div className="text-xs text-slate-500 dark:text-slate-400">{p.email}</div>}
                  </DataCardRow>
                  <DataCardRow label={t('projects.table.country')}>
                    {p.country || t('common.notAvailable')}
                  </DataCardRow>
                  <DataCardRow label={t('projects.table.status')}>
                    <span className="flex items-center justify-end gap-2">
                      <Switch
                        checked={p.isActive !== false}
                        onChange={(value) => toggleProjectActive(p, value)}
                        disabled={togglingId === p.projectId}
                        label={t('projects.fields.activeProject')}
                      />
                      <span
                        className={cn(
                          'text-xs font-medium',
                          p.isActive === false
                            ? 'text-rose-600 dark:text-rose-400'
                            : 'text-emerald-600 dark:text-emerald-400',
                        )}
                      >
                        {p.isActive === false ? t('projects.badges.disabled') : t('projects.badges.active')}
                      </span>
                    </span>
                  </DataCardRow>
                </div>
                <DataCardActions>
                  <Button variant="secondary" size="sm" onClick={() => openRolesModal(p)}>
                    {t('projects.rowActions.roles')}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => openEdit(p)}>
                    {t('projects.rowActions.edit')}
                  </Button>
                  <Button size="sm" onClick={() => handleAccess(p)}>
                    {t('projects.rowActions.access')}
                  </Button>
                </DataCardActions>
              </DataCard>
            ))
          )}
        </div>

        <div className="mt-4 hidden overflow-x-auto md:block">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-slate-500 dark:text-slate-400">
              <tr>
                <th className="py-2 pr-4 font-semibold">{t('projects.table.name')}</th>
                <th className="py-2 pr-4 font-semibold">{t('projects.table.code')}</th>
                <th className="py-2 pr-4 font-semibold">{t('projects.table.sector')}</th>
                <th className="py-2 pr-4 font-semibold">{t('projects.table.contact')}</th>
                <th className="py-2 pr-4 font-semibold">{t('projects.table.country')}</th>
                <th className="py-2 pr-4 font-semibold">{t('projects.table.test')}</th>
                <th className="py-2 pr-4 font-semibold">{t('projects.table.status')}</th>
                <th className="py-2 pr-0 font-semibold text-right">{t('projects.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && page.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={8}>
                    {t('projects.loadingProjects')}
                  </td>
                </tr>
              ) : page.length === 0 ? (
                <tr>
                  <td className="py-8 text-center text-slate-500 dark:text-slate-400" colSpan={8}>
                    {t('common.noResults')}
                  </td>
                </tr>
              ) : (
                page.map((p) => (
                  <tr key={p.projectId} className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-slate-900 dark:text-slate-100">{p.name}</div>
                      {p.description && (
                        <div className="mt-0.5 text-xs text-slate-500 line-clamp-1 dark:text-slate-400">{p.description}</div>
                      )}
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs text-slate-600 dark:text-slate-400">{p.code}</td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{p.sector || t('common.notAvailable')}</td>
                    <td className="py-3 pr-4">
                      <div className="text-slate-900 dark:text-slate-100">{p.contactPerson || t('common.notAvailable')}</div>
                      {p.contactNo && (
                        <div className="text-xs text-slate-500 dark:text-slate-400">{p.contactNo}</div>
                      )}
                      {p.email && (
                        <div className="text-xs text-slate-500 dark:text-slate-400">{p.email}</div>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-slate-700 dark:text-slate-300">{p.country || t('common.notAvailable')}</td>
                    <td className="py-3 pr-4">
                      <Badge tone={p.isTest ? 'amber' : 'green'}>
                        {p.isTest ? t('projects.badges.test') : t('projects.badges.live')}
                      </Badge>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={p.isActive !== false}
                          onChange={(value) => toggleProjectActive(p, value)}
                          disabled={togglingId === p.projectId}
                          label={t('projects.fields.activeProject')}
                        />
                        <span
                          className={cn(
                            'text-xs font-medium',
                            p.isActive === false
                              ? 'text-rose-600 dark:text-rose-400'
                              : 'text-emerald-600 dark:text-emerald-400',
                          )}
                        >
                          {p.isActive === false ? t('projects.badges.disabled') : t('projects.badges.active')}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 pr-0">
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => openRolesModal(p)}>
                          {t('projects.rowActions.roles')}
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => openEdit(p)}>
                          {t('projects.rowActions.edit')}
                        </Button>
                        <Button size="sm" onClick={() => handleAccess(p)}>
                          {t('projects.rowActions.access')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <Pagination
            total={total}
            limit={limit}
            skip={skip}
            onChange={handlePageChange}
          />
        </div>
      </div>

      {/* ── Project Add/Edit Modal ── */}
      <Modal
        open={modalOpen}
        title={draft.projectId ? t('projects.editProject') : t('projects.addProject')}
        onClose={() => (saving ? null : setModalOpen(false))}
        size={draft.projectId ? 'sm' : 'xl'}
      >
        <div className="space-y-4">
          {error ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          ) : null}

          <TextField
            label={t('projects.fields.name')}
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder={t('projects.fields.namePlaceholder')}
          />

          <TextField
            label={t('projects.fields.code')}
            value={draft.code}
            onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
            placeholder={t('projects.fields.codePlaceholder')}
          />

          <TextField
            label={t('projects.fields.description')}
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            placeholder={t('projects.fields.descriptionPlaceholder')}
          />

          <TextField
            label={t('projects.fields.sector')}
            value={draft.sector}
            onChange={(e) => setDraft((d) => ({ ...d, sector: e.target.value }))}
            placeholder={t('projects.fields.sectorPlaceholder')}
          />

          <TextField
            label={t('projects.fields.address')}
            value={draft.address}
            onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
            placeholder={t('projects.fields.addressPlaceholder')}
          />

          {!draft.projectId && (
            <TextField
              label={t('projects.fields.email')}
              type="email"
              value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              placeholder={t('projects.fields.emailPlaceholder')}
            />
          )}

          <TextField
            label={t('projects.fields.contactPerson')}
            value={draft.contactPerson}
            onChange={(e) => setDraft((d) => ({ ...d, contactPerson: e.target.value }))}
            placeholder={t('projects.fields.contactPersonPlaceholder')}
          />

          <TextField
            label={t('projects.fields.contactNo')}
            value={draft.contactNo}
            onChange={(e) => setDraft((d) => ({ ...d, contactNo: e.target.value }))}
            placeholder={t('projects.fields.contactNoPlaceholder')}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('projects.fields.country')}</div>
              <div className="flex flex-wrap gap-2">
                {(['id', 'sg', 'my'] as const).map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, country: code.toUpperCase() }))}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition',
                      draft.country === code.toUpperCase()
                        ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                    )}
                  >
                    {t(`projects.countries.${code}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{t('projects.fields.currency')}</div>
              <div className="flex flex-wrap gap-2">
                {['IDR', 'SGD', 'USD'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, currency: opt }))}
                    className={cn(
                      'rounded-full border px-3 py-1.5 text-xs font-medium transition',
                      draft.currency === opt
                        ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                    )}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            <Switch
              checked={draft.isTest}
              onChange={(value) => setDraft((d) => ({ ...d, isTest: value }))}
              label={t('projects.fields.testProject')}
            />
            <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
              {t('projects.fields.testProject')}
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700">
            <Switch
              checked={draft.isActive}
              onChange={(value) => setDraft((d) => ({ ...d, isActive: value }))}
              label={t('projects.fields.activeProject')}
              className="mt-0.5"
            />
            <div>
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {t('projects.fields.activeProject')}
              </div>
              <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                {t('projects.fields.activeProjectHint')}
              </div>
            </div>
          </div>

          {!draft.projectId && (
            <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {t('projects.seedRoles.title')}
              </div>
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {t('projects.seedRoles.subtitle')}
              </div>

              <div className="mt-3 space-y-3">
                {roleSeeds.map((seed) => (
                  <div
                    key={seed.role}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-semibold capitalize text-slate-900 dark:text-slate-100">
                        {seed.role}
                      </span>
                      <span className="font-mono text-xs text-slate-500 dark:text-slate-400">
                        {seedUserEmail(draft.code, seed.role)}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-5">
                      {MODULE_FLAGS.map((f) => (
                        <label
                          key={f.key}
                          className={cn(
                            'flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-2 text-xs font-medium transition-colors',
                            seed[f.key]
                              ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:border-slate-600',
                          )}
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-slate-300 text-red-600 focus:ring-red-500"
                            checked={seed[f.key]}
                            onChange={(e) => toggleSeedModule(seed.role, f.key, e.target.checked)}
                          />
                          {moduleLabel(f.key)}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {seedsBooking && (
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                  {t('projects.seedRoles.bookingHint')}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 [&>button]:flex-1 sm:[&>button]:flex-none">
            <Button
              variant="secondary"
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={saveDraft} disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Roles List Modal ── */}
      <Modal
        open={rolesModalOpen}
        title={t('projects.roles.title', { project: rolesProject?.name ?? t('nav.items.projects') })}
        onClose={() => setRolesModalOpen(false)}
        size="xl"
      >
        <div className="space-y-4">
          {roleError && !roleModalOpen ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {roleError}
            </div>
          ) : null}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {rolesLoading ? t('projects.roles.loadingRoles') : t('projects.roles.rolesConfigured', { count: roles.length })}
            </div>
            <Button size="sm" onClick={openCreateRole}>{t('projects.roles.addRole')}</Button>
          </div>

          {/* Mobile role cards */}
          {!rolesLoading && roles.length > 0 && (
            <div className="space-y-3 md:hidden">
              {roles.map((r) => (
                <DataCard key={r.roleAccessId}>
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate font-medium text-slate-900 dark:text-slate-100">
                      {r.role}
                    </span>
                    <Badge tone="slate">{enabledCount(r)}/{MODULE_FLAGS.length}</Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {MODULE_FLAGS.map((f) => (
                      <span
                        key={f.key}
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                          (r as any)[f.key]
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                            : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
                        )}
                      >
                        <span
                          className={cn(
                            'h-1.5 w-1.5 rounded-full',
                            (r as any)[f.key] ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600',
                          )}
                        />
                        {moduleLabel(f.key)}
                      </span>
                    ))}
                  </div>
                  <DataCardActions>
                    <Button variant="secondary" size="sm" onClick={() => openEditRole(r)}>
                      {t('common.edit')}
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => deleteRole(r)}>
                      {t('common.delete')}
                    </Button>
                  </DataCardActions>
                </DataCard>
              ))}
            </div>
          )}

          {!rolesLoading && roles.length > 0 && (
            <div className="hidden overflow-x-auto rounded-lg border border-slate-200 md:block dark:border-slate-700">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-3 py-2 font-semibold">{t('projects.roles.roleName')}</th>
                    {MODULE_FLAGS.map((f) => (
                      <th key={f.key} className="px-2 py-2 text-center font-semibold">{moduleLabel(f.key)}</th>
                    ))}
                    <th className="px-3 py-2 text-right font-semibold">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {roles.map((r) => (
                    <tr key={r.roleAccessId} className="hover:bg-slate-50 dark:hover:bg-slate-800/60">
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 dark:text-slate-100">{r.role}</span>
                          <Badge tone="slate">{enabledCount(r)}/{MODULE_FLAGS.length}</Badge>
                        </div>
                      </td>
                      {MODULE_FLAGS.map((f) => (
                        <td key={f.key} className="px-2 py-2.5 text-center">
                          {(r as any)[f.key] ? (
                            <span className="inline-block h-4 w-4 rounded-full bg-emerald-500" title={t('projects.roles.enabled')} />
                          ) : (
                            <span className="inline-block h-4 w-4 rounded-full bg-slate-200 dark:bg-slate-700" title={t('projects.roles.disabled')} />
                          )}
                        </td>
                      ))}
                      <td className="px-3 py-2.5">
                        <div className="flex justify-end gap-2">
                          <Button variant="secondary" size="sm" onClick={() => openEditRole(r)}>
                            {t('common.edit')}
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => deleteRole(r)}>
                            {t('common.delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!rolesLoading && roles.length === 0 && (
            <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              {t('projects.roles.noRolesConfigured')}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Role Add/Edit Modal ── */}
      <Modal
        open={roleModalOpen}
        title={roleEditing?.roleAccessId ? t('projects.roles.editRole') : t('projects.roles.addRole')}
        onClose={() => (roleSaving ? null : setRoleModalOpen(false))}
      >
        {roleEditing && (
          <div className="space-y-4">
            {roleError ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
                {roleError}
              </div>
            ) : null}

            <TextField
              label={t('projects.roles.roleName')}
              value={roleEditing.role}
              onChange={(e) => setRoleEditing((d) => d ? { ...d, role: e.target.value } : d)}
              placeholder={t('projects.roles.roleNamePlaceholder')}
            />

            <div>
              <div className="mb-2 text-sm font-medium text-slate-800 dark:text-slate-200">{t('projects.roles.modulePermissions')}</div>
              <div className="grid grid-cols-2 gap-2">
                {MODULE_FLAGS.map((f) => (
                  <label
                    key={f.key}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border px-3 py-2.5 transition-colors cursor-pointer sm:gap-2.5 sm:px-4 sm:py-3',
                      roleEditing[f.key]
                        ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/40'
                        : 'border-slate-200 bg-slate-50 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                      checked={roleEditing[f.key]}
                      onChange={(e) =>
                        setRoleEditing((d) => d ? { ...d, [f.key]: e.target.checked } : d)
                      }
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{moduleLabel(f.key)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 [&>button]:flex-1 sm:[&>button]:flex-none">
              <Button
                variant="secondary"
                onClick={() => setRoleModalOpen(false)}
                disabled={roleSaving}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={saveRole} disabled={roleSaving}>
                {roleSaving ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
